import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Envelope, TestTube } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SMTPPage = () => {
  const [settings, setSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    from_email: '',
    from_name: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const [hasExistingPassword, setHasExistingPassword] = useState(false);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/smtp`);
      if (data && Object.keys(data).length > 1) {
        setHasExistingPassword(!!data.has_password);
        setSettings({
          host: data.host || '',
          port: data.port || 587,
          username: data.username || '',
          password: '',
          use_tls: data.use_tls !== false,
          from_email: data.from_email || '',
          from_name: data.from_name || ''
        });
      }
    } catch (e) {
      console.error('Failed to fetch SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/smtp`, settings);
      toast.success('SMTP settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API_URL}/api/settings/smtp/test`, { test_email: testEmail });
      toast.success('Test email sent! Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">SMTP Configuration</h1>
        <p className="text-muted-foreground">Configure email delivery settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Envelope size={20} className="text-primary" />
            Email Server Settings
          </CardTitle>
          <CardDescription>
            Configure your SMTP server for sending emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMTP Host</Label>
                <Input
                  value={settings.host}
                  onChange={(e) => setSettings({...settings, host: e.target.value})}
                  placeholder="smtp.gmail.com"
                  required
                  data-testid="smtp-host"
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={settings.port}
                  onChange={(e) => setSettings({...settings, port: parseInt(e.target.value)})}
                  placeholder="587"
                  required
                  data-testid="smtp-port"
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={settings.username}
                  onChange={(e) => setSettings({...settings, username: e.target.value})}
                  placeholder="your-email@gmail.com"
                  required
                  data-testid="smtp-username"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={settings.password}
                  onChange={(e) => setSettings({...settings, password: e.target.value})}
                  placeholder={hasExistingPassword ? "Leave blank to keep current" : "Enter password"}
                  data-testid="smtp-password"
                />
                {hasExistingPassword && !settings.password && (
                  <p className="text-xs text-muted-foreground">Password is saved. Leave blank to keep it unchanged.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>From Email</Label>
                <Input
                  type="email"
                  value={settings.from_email}
                  onChange={(e) => setSettings({...settings, from_email: e.target.value})}
                  placeholder="noreply@example.com"
                  required
                  data-testid="smtp-from-email"
                />
              </div>
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  value={settings.from_name}
                  onChange={(e) => setSettings({...settings, from_name: e.target.value})}
                  placeholder="Branch Manager"
                  required
                  data-testid="smtp-from-name"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={settings.use_tls}
                onCheckedChange={(checked) => setSettings({...settings, use_tls: checked})}
                data-testid="smtp-tls"
              />
              <Label>Use TLS</Label>
            </div>

            <Button type="submit" disabled={saving} data-testid="save-smtp-btn">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TestTube size={20} className="text-primary" />
            Test Configuration
          </CardTitle>
          <CardDescription>
            Send a test email to verify your settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>Test Email Address</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                data-testid="test-email-input"
              />
            </div>
            <Button onClick={handleTest} disabled={testing} variant="outline" data-testid="send-test-email-btn">
              {testing ? 'Sending...' : 'Send Test Email'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SMTPPage;
